/** The routes of the operations views (INC-0610): Data, Research and Operations. */
import type { Route } from "../app/router.ts";
import { hashOf } from "../app/router.ts";
import type { ViewContext, ViewFactory, ViewHandle } from "../app/view.ts";
import { mountCatalysts } from "./catalysts/index.ts";
import { mountData } from "./data/index.ts";
import { mountIncidents } from "./incidents/index.ts";
import { mountInfra } from "./infra/index.ts";
import { mountJournal } from "./journal/index.ts";
import { mountMemory } from "./memory/index.ts";
import { mountLanding } from "./ops-common.ts";
import { mountRecovery } from "./recovery/index.ts";
import { mountRegistry } from "./registry/index.ts";
import { mountReplay } from "./replay/index.ts";
import { mountNotFound } from "./stubs.ts";

const research = (ctx: ViewContext): ViewHandle =>
  mountLanding(ctx, "research", "Research", [
    [
      hashOf("research", "journal"),
      "Journal and evidence",
      "the economic journal and its timeline",
    ],
    [hashOf("research", "replay"), "Replay and experiments", "registered runs, read-only"],
  ]);

const operations = (ctx: ViewContext): ViewHandle =>
  mountLanding(ctx, "operations", "Operations", [
    [
      hashOf("operations", "incidents"),
      "Incidents and alert routes",
      "acknowledgement and external status",
    ],
    [
      hashOf("operations", "infra"),
      "Infrastructure and releases",
      "inventory, trends, release, freeze",
    ],
    [hashOf("operations", "memory"), "Memory and workload", "pressure ladder and workload"],
    [
      hashOf("operations", "recovery"),
      "Backup and recovery",
      "uploaded, verified and restore epoch",
    ],
    [hashOf("operations", "registry"), "Strategy and configuration", "saved and active settings"],
  ]);

const SUBS: ReadonlyMap<string, ReadonlyMap<string | null, ViewFactory>> = new Map([
  [
    "data",
    new Map([
      [null, mountData],
      ["catalysts", mountCatalysts],
    ]),
  ],
  [
    "research",
    new Map([
      [null, research],
      ["journal", mountJournal],
      ["replay", mountReplay],
    ]),
  ],
  [
    "operations",
    new Map([
      [null, operations],
      ["incidents", mountIncidents],
      ["infra", mountInfra],
      ["memory", mountMemory],
      ["recovery", mountRecovery],
      ["registry", mountRegistry],
    ]),
  ],
]);

/** Resolve a route of the data, research and operations areas, or `null` for any other area. */
export function resolveOps(route: Route): ViewFactory | null {
  const area = SUBS.get(route.area);
  return area === undefined ? null : (area.get(route.sub) ?? mountNotFound);
}
